/**
 * 영업건 통합 검색·선택 드롭다운.
 *
 * 매출일련번호 / 제안사 / 엔드유저 / 프로젝트명을 한 칸에서 통합 검색(GET /sales?q=) →
 * 결과 목록에서 영업건 1건 선택. 출고등록 폼·승인 모달 공용.
 *
 * 선택 시 onSelect 로 그 영업건 행을 넘김(엔드유저/제안사/프로젝트명/상태 포함) →
 * 호출 측에서 납품처·제안사·프로젝트명·sales_id 를 채움.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as salesApi from '../lib/sales-api';
import { statusTagClass } from '../lib/sales-stage';
import type { SalesListRow, SalesStatus } from '../lib/types';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  /** 현재 선택된 영업건 (없으면 null) — 선택 칩 표시용 */
  selected: { salesId: string; endUser: string | null; proposalCompany: string | null; projectName: string | null; status: string } | null;
  onSelect: (row: SalesListRow) => void;
  onClear: () => void;
  placeholder?: string;
  /** 진행단계 제한 — 지정 시 이 상태들만 검색 결과에 노출 (서버 stage 필터 + 클라이언트 재확인). */
  stages?: SalesStatus[];
}

export function SalesSearchSelect({ selected, onSelect, onClear, placeholder, stages }: Props) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 입력 디바운스 (300ms)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(id);
  }, [term]);

  const q = useQuery({
    queryKey: ['sales', 'search', debounced, stages ?? []],
    queryFn: () => salesApi.listSales({ q: debounced, pageSize: 20, stage: stages }),
    enabled: open && debounced.length >= 1,
    staleTime: 30_000,
  });
  // 서버 stage 필터는 '이상 미완료 건'을 항상 섞어 반환할 수 있어, 클라이언트에서 상태 재확인.
  const rows = (q.data?.data ?? []).filter((r) => !stages || stages.includes(r.status));

  // 바깥 클릭 시 닫기
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (selected) {
    // 단계면 영업진행과 같은 색 · 「현재 연결」(수출입 라인 수정 창) 같은 안내 글자는 예전 회색 · 빈 값이면 안 그림(전엔 빈 회색 칸)
    const selectedStageClass = statusTagClass(selected.status);
    return (
      <div className="flex items-start justify-between gap-2 border border-blue-300 bg-white rounded px-2 py-1.5">
        <div className="min-w-0 text-sm">
          <div>
            <span className="font-mono font-semibold text-blue-700">{selected.salesId}</span>
            <span className="ml-2 text-slate-700">{selected.endUser ?? '-'}</span>
            {selected.proposalCompany && (
              <span className="ml-1 text-slate-500">· {selected.proposalCompany}</span>
            )}
            {selectedStageClass && (
              <span className={`ml-1.5 ${selectedStageClass}`}>{selected.status}</span>
            )}
          </div>
          {selected.projectName && (
            <div className="text-xs text-slate-500 mt-0.5 truncate">{selected.projectName}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-slate-400 hover:text-rose-600 text-sm flex-shrink-0"
          title="영업건 선택 해제"
        >
          ✕ 해제
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? '매출번호 / 제안사 / 엔드유저 / 프로젝트명 검색'}
        className="ui-input w-full border-blue-300"
      />
      {open && term.trim().length >= 1 && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-300 rounded shadow-lg z-40 max-h-72 overflow-y-auto">
          {q.isLoading && <div className="px-3 py-2 text-xs text-slate-500"><LoadingLines /></div>}
          {!q.isLoading && rows.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">검색 결과 없음</div>
          )}
          {rows.map((r) => (
            <button
              key={r.salesId}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(r);
                setTerm('');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-sm">
                <span className="font-mono font-semibold text-blue-700">{r.salesId}</span>
                <span className="ml-2 text-slate-700">{r.endUser ?? '-'}</span>
                {r.proposalCompany && <span className="ml-1 text-slate-500">· {r.proposalCompany}</span>}
                <span className={`ml-1.5 ${statusTagClass(r.status) ?? ''}`}>{r.status}</span>
              </div>
              {r.projectName && (
                <div className="text-xs text-slate-500 mt-0.5">
                  <span className="text-slate-400">프로젝트: </span>{r.projectName}
                </div>
              )}
              {r.itemSummary && (
                <div className="text-xs text-slate-600 mt-0.5 break-words whitespace-pre-wrap">
                  <span className="text-slate-400">제안품목: </span>{r.itemSummary}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
